import {
  Box,
  chakra,
  Checkbox,
  CheckboxGroup,
  Divider,
  Flex,
  FormLabel,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  useBreakpointValue
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { nonNumericSymbols, translateSubElementString } from "../../../../../utils";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EsgLabel, FormInput, FormSelect, NumericFormInput } from "../../../../../components/Forms";
import * as React from "react";
import { areArraysEqual } from "../../../helpers";
import { Answer } from "../../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";
import { QuestionIconTooltip } from "../../../../../components";

function ProductDetails({
                          currentElement,
                          tabKey,
                          callMethod,
                          isPreview
                        }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierQuestionnaire.product.productDetails"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const formId = `navForm${tabKey}`;
  const currentProduct = currentElement;

  const [emissionsInclusions, setEmissionsInclusions] = useState([]);
  const [isEstimatedData, setIsEstimatedData] = useState("true");
  const [isVerifiedData, setIsVerifiedData] = useState("false");

  const [isDirty, setIsDirty] = useState(false);

  const productFormDefaultValues = {
    id: currentProduct.id,
    description: currentProduct.description,
    quantity: currentProduct.quantity,
    unit: currentProduct.unit,
    totalCo2Emissions: currentProduct.totalCo2Emissions,
    calculationsMethodology: currentProduct.calculationsMethodology,
    dataVerifiedBy: currentProduct.dataVerifiedBy
  };

  const {
    register,
    handleSubmit,
    formState,
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: productFormDefaultValues
  });

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      callMethod({ isFormValid: false });
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, isDirty, callMethod]);

  const unitValue = watch("unit");
  const calcMethodologyValue = watch("calculationsMethodology");

  useEffect(() => {
    setEmissionsInclusions(currentProduct.emissionsInclusions);
    setIsEstimatedData(currentProduct.estimateOrCollectedFromSupplyChain === null ||
    currentProduct.estimateOrCollectedFromSupplyChain === true ? "true" : "false");
    setIsVerifiedData(currentProduct.dataVerified === true ? "true" : "false");
  }, [currentProduct.emissionsInclusions, currentProduct.estimateOrCollectedFromSupplyChain, currentProduct.dataVerified]);

  const onSubmit = (data) => {
    if (!isPreview) {
      const apiData = data;
      apiData.id = currentProduct.id;
      apiData.emissionsInclusions = emissionsInclusions;
      apiData.estimateOrCollectedFromSupplyChain = isEstimatedData === "true";
      apiData.dataVerified = isVerifiedData === "true";

      if (isDirty || !areArraysEqual(apiData.emissionsInclusions, currentProduct.emissionsInclusions) ||
        apiData.estimateOrCollectedFromSupplyChain !== currentProduct.estimateOrCollectedFromSupplyChain ||
        apiData.dataVerified !== currentProduct.dataVerified) {
        callMethod(apiData);
      } else {
        callMethod(null);
      }
      reset(data);
    }
  };

  const containerDirection = useBreakpointValue({
    xl: "row",
    base: "column"
  });

  return <chakra.form
    id={formId}
    onSubmit={handleSubmit(onSubmit)}
    flexGrow="1"
    overflowY="auto"
    paddingBlockEnd="4"
    paddingInline="2"
    marginInline="-2"
    height="100%"
  >
    <Flex direction="column" gap={5}>
      {isPreview ?
        /* Start of preview part */
        <Fragment>
          <Heading size="sm">{t("checkProductDetails")}</Heading>
          <Flex direction="column" gap={10}>
            <Box>
              <EsgLabel text={`${t("inputs.quantityOfProduct")} *`} />
              <Answer answerText={productFormDefaultValues.quantity} />
            </Box>
            <Box>
              <EsgLabel text={`${t(`inputs.unit`)} *`} />
              <Answer answerText={productFormDefaultValues.unit && productFormDefaultValues.unit !== "" ?
                globalTranslation(`measures.${productFormDefaultValues.unit.toLowerCase()}`) : ""} />
            </Box>
            <Box>
              <EsgLabel text={translateSubElementString(
                "supplierQuestionnaire.product.productDetails.inputs.totalEmissionsPerProduct")} />
              <Answer answerText={productFormDefaultValues.totalCo2Emissions} />
            </Box>
            <Box>
              <EsgLabel text={`${t(`inputs.calculationsMethodology`)} *`} />
              <Answer answerText={productFormDefaultValues.calculationsMethodology} />
            </Box>
            <Box>
              <EsgLabel text={t("inputs.emissionsInclusions")} />
              <Answer answerText={emissionsInclusions && emissionsInclusions.length > 0 ?
                emissionsInclusions.map(type =>
                  globalTranslation(`supplierQuestionnaire.emissionsInclusionOptions.${type}`)
                ).join(", ") : ""} />
            </Box>
            <Box>
              <EsgLabel text={`${t("inputs.estimateOrCollectedFromSupplyChain")} *`} />
              <Answer answerText={isEstimatedData === "true" ?
                t("inputs.estimatedData") : t("inputs.primaryData")} />
            </Box>
            <Box>
              <Box>
                <EsgLabel text={`${t("inputs.dataVerified")} *`} />
                <Answer answerText={isVerifiedData === "true" ?
                  t("inputs.yes") : t("inputs.no")} />
              </Box>
              <Box>
                <EsgLabel text={`${t("inputs.dataVerifiedBy")} ${isVerifiedData === "true" ? "*" : ""}`} />
                <Answer answerText={productFormDefaultValues.dataVerifiedBy} />
              </Box>
            </Box>
          </Flex>
        </Fragment>

        /* End of preview part */
        :
        <Fragment>
          <Heading size="sm">{t("provideProductDetails")}</Heading>
          <Flex gap={5} flexDirection={containerDirection}>
            <NumericFormInput
              label={
                <Flex alignItems="top" title={`${t("inputs.quantityOfProduct")} *`}>
                  {`${t("inputs.quantityOfProduct")} *`}
                  <QuestionIconTooltip
                    label={t("inputs.quantityOfProductTooltip")}
                    ariaLabel={"prod-quantity-tooltip"}
                    height={"unset"}
                  />
                </Flex>
              }
              errors={formState.errors}
              {...register("quantity", {
                required: globalTranslation("entityRequired", { entityName: t("inputs.quantityOfProduct") })
              })}
              onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
            />
            <FormSelect
              label={
                <Flex alignItems="top" title={`${t("inputs.unit")} *`}>
                  {`${t("inputs.unit")} *`}
                  <QuestionIconTooltip
                    label={t("inputs.unitTooltip")}
                    ariaLabel={"unit-tooltip"}
                    height={"unset"}
                  />
                </Flex>
              }
              errors={formState.errors}
              {...register("unit", {
                required: globalTranslation("entityRequired", { entityName: t("inputs.unit") })
              })}
              placeholder=" "
              title={unitValue && unitValue !== "" ? globalTranslation(`measures.${unitValue.toLowerCase()}`) : ""}
            >
              {currentProduct.structure?.units?.map(unit => {
                return <option key={unit} value={unit}>
                  {globalTranslation(`measures.${unit.toLowerCase()}`)}
                </option>;
              }) ?? []}
            </FormSelect>
            <NumericFormInput
              label={translateSubElementString(
                "supplierQuestionnaire.product.productDetails.inputs.totalEmissionsPerProduct")}
              title={`${t("inputs.totalEmissionsEntity")} (CO2e) *`}
              errors={formState.errors}
              {...register("totalCo2Emissions", {
                required: globalTranslation("entityRequired", { entityName: t("inputs.totalEmissionsEntity") })
              })}
              onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
            />
          </Flex>
          <FormInput
            label={
              <Flex alignItems="top" title={`${t(`inputs.calculationsMethodology`)} *`}>
                {`${t(`inputs.calculationsMethodology`)} *`}
                <QuestionIconTooltip
                  label={t("inputs.calcMethodologyTooltip")}
                  ariaLabel={"calc-method-tooltip"}
                  height={"unset"}
                />
              </Flex>
            }
            errors={formState.errors}
            {...register("calculationsMethodology", {
              required: globalTranslation("entityRequired", { entityName: t("inputs.calcMethodologyRequired") })
            })}
            title={calcMethodologyValue}
            maxInlineSize="fit-content"
            maxWidth="100%"
          />
          <Box>
            <FormLabel
              htmlFor={"emissionsInclusionsGroup"}
              opacity="0.75"
              fontSize="xs"
              whiteSpace="nowrap"
              marginBlockEnd="2"
              blockSize="5"
              isTruncated
              color="esg_gray.200"
              title={t("inputs.emissionsInclusions")}
            >
              {t("inputs.emissionsInclusions")}
            </FormLabel>
            <CheckboxGroup
              id={"emissionsInclusionsGroup"}
              colorScheme="customScheme"
              value={emissionsInclusions}
              onChange={(e) => setEmissionsInclusions(e)}
            >
              <Checkbox
                width="max-content" maxWidth="100%"
                isChecked={emissionsInclusions.length === currentProduct.structure?.emissionsInclusions?.length}
                onChange={(e) => {
                  if (emissionsInclusions.length === currentProduct.structure?.emissionsInclusions?.length) {
                    setEmissionsInclusions([]);
                  } else {
                    setEmissionsInclusions(currentProduct.structure?.emissionsInclusions ?? []);
                  }
                }}
              >
                {t("inputs.selectAll")}
              </Checkbox>
              <Divider marginBlock={1} width="150px" />
              <Stack spacing={1} direction={"column"}>
                {currentProduct.structure?.emissionsInclusions?.map(type => {
                  return <Checkbox width="max-content" maxWidth="100%" key={type} value={type}>
                    {globalTranslation(`supplierQuestionnaire.emissionsInclusionOptions.${type}`)}
                  </Checkbox>;
                }) ?? null}
              </Stack>
            </CheckboxGroup>
          </Box>
          <Box>
            <FormLabel
              htmlFor={"isEstimatedDataGroup"}
              opacity="0.75"
              fontSize="xs"
              whiteSpace="nowrap"
              marginBlockEnd="2"
              blockSize="5"
              isTruncated
              color="esg_gray.200"
              title={`${t("inputs.estimateOrCollectedFromSupplyChain")} *`}
            >
              {`${t("inputs.estimateOrCollectedFromSupplyChain")} *`}
            </FormLabel>
            <RadioGroup id={"isEstimatedDataGroup"} value={isEstimatedData} mb={5} style={{ position: "relative" }}>
              <Stack spacing={5} direction="row">
                <Radio
                  colorScheme="customScheme"
                  value={"false"}
                  onChange={e => {
                    setIsEstimatedData(e?.target.value);
                  }}
                >
                  <Flex alignItems="top">
                    {t("inputs.primaryData")}
                    <QuestionIconTooltip
                      label={t("inputs.primaryDataTooltip")}
                      ariaLabel={"primary-data-tooltip"}
                      height={"unset"}
                    />
                  </Flex>
                </Radio>
                <Radio
                  colorScheme="customScheme"
                  value={"true"}
                  onChange={e => {
                    setIsEstimatedData(e?.target.value);
                  }}
                >
                  <Flex alignItems="top">
                    {t("inputs.estimatedData")}
                    <QuestionIconTooltip
                      label={t("inputs.estimatedDataTooltip")}
                      ariaLabel={"estimated-data-tooltip"}
                      height={"unset"}
                    />
                  </Flex>
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>
          <Box>
            <Flex alignItems="top" marginBlockEnd="2">
              <FormLabel
                htmlFor={"isDataVerified"}
                opacity="0.75"
                fontSize="xs"
                whiteSpace="nowrap"
                marginBlockEnd="0"
                marginInlineEnd="0"
                blockSize="5"
                isTruncated
                color="esg_gray.200"
                title={`${t("inputs.dataVerified")} *`}
              >
                {`${t("inputs.dataVerified")} *`}
              </FormLabel>
              <QuestionIconTooltip
                label={t("inputs.dataVerifiedTooltip")}
                ariaLabel={"data-verified-tooltip"}
                height={"unset"}
              />
            </Flex>
            <RadioGroup id={"isDataVerified"} value={isVerifiedData} mb={5} style={{ position: "relative" }}>
              <Stack spacing={5} direction="row">
                <Radio
                  colorScheme="customScheme"
                  value={"true"}
                  onChange={e => {
                    setIsVerifiedData(e?.target.value);
                  }}
                >
                  {t("inputs.yes")}
                </Radio>
                <Radio
                  colorScheme="customScheme"
                  value={"false"}
                  onChange={e => {
                    setValue("dataVerifiedBy", "", { shouldDirty: true });
                    setIsVerifiedData(e?.target.value);
                  }}
                >
                  {t("inputs.no")}
                </Radio>
              </Stack>
            </RadioGroup>
            <FormInput
              label={`${t("inputs.dataVerifiedBy")} ${isVerifiedData === "true" ? "*" : ""}`}
              errors={formState.errors}
              {...register("dataVerifiedBy", {
                required: isVerifiedData === "true" ? globalTranslation("errors.required") : false
              })}
              isDisabled={isVerifiedData === "false"}
            />
          </Box>
        </Fragment>
      }
    </Flex>
  </chakra.form>;
}

export default ProductDetails;